import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

/**
 * iPad/iPhone: Hls.isSupported() puede ser true, pero Safari reproduce mejor
 * con HLS nativo del <video> (mismo criterio que el player de MediaMTX).
 */
function esDispositivoApple() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  // iPadOS 13+ se reporta como MacIntel con touch
  if (
    navigator.platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1
  ) {
    return true;
  }
  return false;
}

/**
 * Reproduce HLS cuando playerType es hls; usa iframe si el backend indica iframe.
 * Usa playbackUrl tal cual (HTTPS de camaras.*); no reescribe a HTTP/IP:8888.
 */
function VideoReproductor({ playbackUrl, playerType, onError }) {
  const videoRef = useRef(null);

  useEffect(() => {
    if (!playbackUrl) return undefined;

    if (playerType === "iframe") {
      return undefined;
    }

    const video = videoRef.current;
    if (!video) return undefined;

    const url = String(playbackUrl).trim();
    if (!url) return undefined;

    const soportaNativo = !!video.canPlayType("application/vnd.apple.mpegurl");
    const usarNativo = soportaNativo && esDispositivoApple();

    // iOS/iPadOS: HLS nativo. Resto: hls.js si está disponible.
    if (!usarNativo && playerType === "hls" && Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        // MediaMTX live: lowLatencyMode agresivo puede cortar en algunos móviles
        lowLatencyMode: false,
      });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data.fatal && onError) {
          onError(data);
        }
      });
      return () => {
        hls.destroy();
      };
    }

    if (soportaNativo) {
      const onVideoError = () => {
        if (onError) {
          onError(new Error("Error nativo al cargar el stream HLS"));
        }
      };
      video.addEventListener("error", onVideoError);
      video.src = url;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay bloqueado: el usuario puede pulsar play en controles
        });
      }
      return () => {
        video.removeEventListener("error", onVideoError);
        video.removeAttribute("src");
        video.load();
      };
    }

    if (onError) {
      onError(new Error("HLS no soportado en este navegador"));
    }
    return undefined;
  }, [playbackUrl, playerType, onError]);

  if (!playbackUrl) {
    return null;
  }

  if (playerType === "iframe") {
    return (
      <iframe
        className="acceso-camara-iframe"
        title="Transmisión"
        src={playbackUrl}
        allow="autoplay; fullscreen"
        allowFullScreen
      />
    );
  }

  return (
    <video
      ref={videoRef}
      className="acceso-camara-video"
      controls
      playsInline
      autoPlay
      muted
    />
  );
}

export default VideoReproductor;
